/**
 * API Client - Axios instance with interceptors
 * 
 * Features:
 * - Auto token injection
 * - Token refresh on 401
 * - Request/Response logging (dev only)
 * - Timeout handling
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// API Base URL - through gateway
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Token keys
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

// Create axios instance
export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token!);
        }
    });
    failedQueue = [];
};

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Dev logging
        if (process.env.NODE_ENV === 'development') {
            console.log(`🌐 ${config.method?.toUpperCase()} ${config.url}`);
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor - Handle errors & token refresh
apiClient.interceptors.response.use(
    (response) => {
        // Dev logging
        if (process.env.NODE_ENV === 'development') {
            console.log(`✅ ${response.config.url} - ${response.status}`);
        }
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        const url = originalRequest?.url || '';

        // Skip token refresh logic for auth endpoints (login, register, etc.)
        // These return 401 for invalid credentials, not expired tokens
        const isAuthEndpoint = url.includes('/api/auth/login') ||
            url.includes('/api/auth/check-availability') ||
            url.includes('/api/clients/register');

        // Handle 401 - Token expired (skip for auth endpoints)
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            if (isRefreshing) {
                // Wait for refresh to complete
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return apiClient(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

            if (!refreshToken) {
                // No refresh token - logout
                clearTokens();
                window.location.href = '/login';
                return Promise.reject(error);
            }

            try {
                const { data } = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
                    refreshToken,
                });

                const newAccessToken = data.accessToken;
                localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);

                if (data.refreshToken) {
                    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
                }

                processQueue(null, newAccessToken);
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

                return apiClient(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError as Error, null);
                clearTokens();
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Log errors in dev
        if (process.env.NODE_ENV === 'development') {
            const isTimeout = error.code === 'ECONNABORTED' || error.message.includes('timeout');
            if (isTimeout) {
                console.error(`⏱️ TIMEOUT: ${originalRequest?.url} timed out after ${originalRequest?.timeout}ms`);
            } else {
                console.error(`❌ ${originalRequest?.url} - ${error.response?.status || 'No Response'}`, error.response?.data || error.message);
            }
        }

        return Promise.reject(error);
    }
);

// Token utilities
export const setTokens = (accessToken: string, refreshToken: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const clearTokens = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const isAuthenticated = () => {
    return !!getAccessToken();
};

export default apiClient;
